import { useState } from 'react';
import { useWorkbook } from '@/context/WorkbookContext';
import { 
  WorkbookPage, 
  RuleHeader, 
  SectionTitle, 
  Paragraph, 
  Callout,
  ExampleBox,
  StepList,
  WorksheetInput,
  CheckboxItem,
  AffirmationBox,
  RecapBox,
  OptionalSection,
  AutoTotal,
  parseMoney,
  formatMoney
} from '@/components/WorkbookPage';
import { PiggyBank } from 'lucide-react';

export const Rule7Page = () => {
  const { formData, updateFormData } = useWorkbook();
  const data = formData.rule7 || {};
  
  const updateData = (field, value) => {
    updateFormData('rule7', { ...data, [field]: value });
  };
  
  const [allocations, setAllocations] = useState(data.allocationPlan || [
    { category: 'Emergency fund', percentage: '', amount: '' },
    { category: 'High-interest debt', percentage: '', amount: '' },
    { category: 'Irregular expenses (sinking fund)', percentage: '', amount: '' },
    { category: 'Necessary purchase/repair', percentage: '', amount: '' },
    { category: 'Something for me', percentage: '', amount: '' },
  ]);
  
  const lumpSum = parseMoney(data.lumpSumAmount);
  const rowAmount = (pct) => lumpSum * parseMoney(pct) / 100;
  let totalPercent = 0;
  for (let i = 0; i < allocations.length; i++) {
    totalPercent += parseMoney(allocations[i].percentage);
  }
  const totalAllocated = lumpSum * totalPercent / 100;

  const updateAllocation = (index, field, value) => {
    const newAllocations = [...allocations];
    newAllocations[index] = { ...newAllocations[index], [field]: value };
    if (field === 'percentage') {
      newAllocations[index].amount = rowAmount(value).toFixed(2);
    }
    setAllocations(newAllocations);
    updateData('allocationPlan', newAllocations);
  };
  
  return (
    <WorkbookPage pageId="rule-7" prevPage="rule-6" nextPage="rule-8">
      <RuleHeader ruleNumber="7" title="Spread Out Lump Sums" section="control" />
      
      <SectionTitle>What This Rule Means</SectionTitle>
      <Paragraph>
        When you receive a lump sum—a tax refund, bonus, inheritance, or back pay—it can feel 
        like a windfall. But without a plan, it often disappears on things that don't really 
        help you. This rule teaches you how to make lump sums work FOR your future.
      </Paragraph>
      
      <SectionTitle>Why It Matters</SectionTitle>
      <Paragraph>
        Studies show most people who receive tax refunds have spent the money within a few weeks—
        often without anything meaningful to show for it. A plan prevents this and turns windfalls 
        into real progress.
      </Paragraph>
      
      <Callout type="tip" title="The 50/30/20 Lump Sum Rule">
        <p>A simple framework:</p>
        <ul className="mt-2 space-y-1">
          <li><strong>50%</strong> — Needs (debt, emergency fund, necessary expenses)</li>
          <li><strong>30%</strong> — Future (savings, investments, big goals)</li>
          <li><strong>20%</strong> — Wants (something for yourself—guilt-free)</li>
        </ul>
      </Callout>
      
      <ExampleBox title="Marcus Gets a $2,400 Tax Refund">
        <p className="mb-3">Instead of spending it all, he splits it:</p>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>50% → Emergency fund (was at $200, now $1,400)</span>
            <span className="font-medium">$1,200</span>
          </div>
          <div className="flex justify-between">
            <span>30% → Extra payment on credit card</span>
            <span className="font-medium">$720</span>
          </div>
          <div className="flex justify-between">
            <span>20% → New shoes + nice dinner out</span>
            <span className="font-medium">$480</span>
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Result: He made real progress AND enjoyed some of it. No guilt, no regret.
        </p>
      </ExampleBox>
      
      <StepList steps={[
        "When you know a lump sum is coming, make the plan BEFORE it arrives.",
        "List your current financial priorities (emergency fund, debt, savings, needs).",
        "Decide percentages for each category.",
        "Include at least a small percentage for yourself—you deserve to enjoy some of it.",
        "Transfer the money to the right accounts AS SOON as you receive it.",
        "Don't let it sit in your checking account where it's easy to spend."
      ]} />
      
      <div className="bg-muted/30 rounded-xl p-6 my-8 border border-border">
        <h3 className="font-serif font-bold text-lg mb-6">
          <PiggyBank className="h-5 w-5 inline mr-2" />
          Your Lump Sum Allocation Plan
        </h3>
        
        <div className="space-y-6">
          <WorksheetInput
            label="Expected lump sum amount (or use a past example):"
            placeholder="$___"
            value={data.lumpSumAmount || ''}
            onChange={(v) => updateData('lumpSumAmount', v)}
          />
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 pr-4">Category</th>
                  <th className="text-right py-2 px-2">%</th>
                  <th className="text-right py-2 pl-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {allocations.map((item, index) => (
                  <tr key={index} className="border-b border-border/50">
                    <td className="py-2 pr-4">
                      <input
                        type="text"
                        className="w-full bg-transparent focus:outline-none"
                        value={item.category}
                        onChange={(e) => updateAllocation(index, 'category', e.target.value)}
                      />
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="text"
                        className="w-full text-right bg-transparent focus:outline-none"
                        placeholder="___%"
                        value={item.percentage}
                        onChange={(e) => updateAllocation(index, 'percentage', e.target.value)}
                      />
                    </td>
                    <td className="py-2 pl-2 text-right">
                      <span className="text-foreground tabular-nums" data-testid={'rule7-amount-' + index}>
                        {parseMoney(item.percentage) > 0 && lumpSum > 0 ? formatMoney(rowAmount(item.percentage)) : '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <AutoTotal
            label="Total allocated"
            value={totalAllocated}
            hint={'You\'ve allocated ' + (Math.round(totalPercent * 100) / 100) + '% of your lump sum'}
            testId="rule7-total-allocated"
          />
          {totalPercent > 100 && (
            <p className="text-sm text-destructive" data-testid="rule7-over-100">
              Heads up: your percentages add up to more than 100%.
            </p>
          )}
        </div>
      </div>
      
      <div className="my-8">
        <h3 className="font-semibold mb-4">Before moving on, confirm:</h3>
        <CheckboxItem checked={data.checklist1 || false} onChange={(v) => updateData('checklist1', v)}>
          I understand why planning for lump sums matters
        </CheckboxItem>
        <CheckboxItem checked={data.checklist2 || false} onChange={(v) => updateData('checklist2', v)}>
          I've created an allocation plan
        </CheckboxItem>
        <CheckboxItem checked={data.checklist3 || false} onChange={(v) => updateData('checklist3', v)}>
          I've included something for myself
        </CheckboxItem>
      </div>
      
      <RecapBox items={[
        "Plan BEFORE the money arrives",
        "Split it: Needs, Future, Wants",
        "Move money immediately to right accounts",
        "Enjoy some guilt-free—you earned it"
      ]} />
      
      <AffirmationBox>
        I use windfalls wisely by planning ahead. I balance progress with enjoying life.
      </AffirmationBox>
      
      <OptionalSection>
        <WorksheetInput
          label="Your thoughts:"
          placeholder="Write anything that comes to mind..."
          value={data.reflection || ''}
          onChange={(v) => updateData('reflection', v)}
          multiline
        />
      </OptionalSection>
    </WorkbookPage>
  );
};
